import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook for API calls with loading, error handling, and abort controller
 * 
 * @param {Function} apiFunction - A function that returns a Promise (e.g., () => getERPSyncOverview(country, period))
 * @param {Array} dependencies - Array of dependencies that trigger a refetch when changed
 * @returns {Object} { data, loading, error, refetch }
 */
const useApi = (apiFunction, dependencies = []) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async (signal) => {
        setLoading(true);
        setData(null);
        setError(null);

        try {
            const result = await apiFunction();

            if (!signal.aborted) {
                setData(result);
            }
        } catch (err) {
            if (!signal.aborted) {
                console.error("API call failed:", err);
                setError(err);
            }
        } finally {
            if (!signal.aborted) {
                setLoading(false);
            }
        }
    }, [apiFunction]);

    useEffect(() => {
        const controller = new AbortController();

        fetchData(controller.signal);

        return () => controller.abort();
    }, dependencies); // eslint-disable-line react-hooks/exhaustive-deps

    // Refetch function that can be called manually
    const refetch = useCallback(() => {
        const controller = new AbortController();
        fetchData(controller.signal);
    }, [fetchData]);

    return { data, loading, error, refetch };
};

export default useApi;
